import { FlashbarProps } from '@amzn/awsui-components-core';
import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface FlashMessage {
  readonly id: string;
  readonly type: FlashbarProps.Type;
  readonly content: ReactNode;
}

interface FlashMessageContextType {
  readonly messages: readonly FlashMessage[];
  addMessage: (message: Omit<FlashMessage, 'id'>) => void;
  removeMessage: (id: string) => void;
}

interface FlashMessageProviderProps {
  readonly children: ReactNode;
}

const FlashMessageContext = createContext<FlashMessageContextType | undefined>(undefined);

export const FlashMessageProvider = ({ children }: FlashMessageProviderProps): JSX.Element => {
  const [messages, setMessages] = useState<FlashMessage[]>([]);

  const addMessage = useCallback((message: Omit<FlashMessage, 'id'>): void => {
    const id = uuidv4();
    setMessages((prev) => [...prev, { ...message, id }]);
  }, []);

  const removeMessage = useCallback((id: string): void => {
    setMessages((prev) => prev.filter((message) => message.id !== id));
  }, []);

  const contextValue: FlashMessageContextType = {
    messages,
    addMessage,
    removeMessage
  };

  return <FlashMessageContext.Provider value={contextValue}>{children}</FlashMessageContext.Provider>;
};

export const useFlashMessage = (): FlashMessageContextType => {
  const context = useContext(FlashMessageContext);
  if (context === undefined) {
    throw new Error('useFlashMessage must be used within a FlashMessageProvider');
  }
  return context;
};
